import { InputCheckbox, InputField, InputText } from "@sellar-io/components";
import { useState } from "react";

export type SelectableProduct = { id: number; name: string | null };

export const ProductSelection = ({
  products,
  selectedIds,
  onChange,
  disabled = false,
}: {
  products: SelectableProduct[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}) => {
  const [search, setSearch] = useState("");
  const visible = products.filter(
    (product) =>
      product.name
        ?.toLocaleLowerCase()
        .includes(search.trim().toLocaleLowerCase()) || !search.trim(),
  );
  const allSelected =
    visible.length > 0 &&
    visible.every((product) => selectedIds.includes(product.id));
  return (
    <div className="flex flex-col gap-2">
      <InputField label="Select products">
        <InputText
          value={search}
          disabled={disabled}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search for a product..."
        />
      </InputField>
      <div className="max-h-64 divide-y divide-zinc-200 overflow-auto rounded border border-zinc-200">
        {visible.length === 0 ? (
          <p className="p-4 text-sm text-zinc-500">No products found.</p>
        ) : (
          visible.map((product) => (
            <label
              key={product.id}
              className="flex cursor-pointer items-center gap-3 p-3 hover:bg-zinc-50"
            >
              <InputCheckbox
                disabled={disabled}
                checked={selectedIds.includes(product.id)}
                onChange={() =>
                  onChange(
                    selectedIds.includes(product.id)
                      ? selectedIds.filter((id) => id !== product.id)
                      : [...selectedIds, product.id],
                  )
                }
              />
              <span className="min-w-0 flex-1 truncate">
                {product.name ?? "Unnamed product"}
              </span>
            </label>
          ))
        )}
      </div>
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-zinc-500" aria-live="polite">
          {selectedIds.length}{" "}
          {selectedIds.length === 1 ? "product" : "products"} selected
        </span>
        {visible.length > 0 && (
          <button
            type="button"
            disabled={disabled}
            className="text-blue-700 underline"
            onClick={() =>
              onChange(
                allSelected
                  ? selectedIds.filter(
                      (id) => !visible.some((product) => product.id === id),
                    )
                  : [
                      ...new Set([
                        ...selectedIds,
                        ...visible.map((product) => product.id),
                      ]),
                    ],
              )
            }
          >
            {allSelected
              ? "Clear selection"
              : search.trim()
                ? "Select results"
                : "Select all"}
          </button>
        )}
      </div>
    </div>
  );
};
